import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { collection, onSnapshot, query, where, orderBy, doc, getDoc, getDocs, documentId } from 'firebase/firestore';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { db } from '../firebaseConfig';
import { parseJobDate } from '../utils/dateUtils';

const SkipsDataContext = createContext();

export const useSkipsData = () => {
    const context = useContext(SkipsDataContext);
    if (!context) {
        throw new Error('useSkipsData must be used within a SkipsDataProvider');
    }
    return context;
};

export const SkipsDataProvider = ({ children }) => {
    const [jobs, setJobs] = useState([]);
    const [companyId, setCompanyId] = useState('');
    const [companyName, setCompanyName] = useState('');
    const [userIsAdmin, setUserIsAdmin] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    // Filter states
    const [filters, setFilters] = useState({
        size: '',
        dateRange: 'all',
        driver: '',
        status: 'all', // 'all', 'delivered', 'collected'
        paymentStatus: ['Paid', 'TO PAY', 'Account', 'Unknown'], // Multi-select array
        wasteType: '', // Waste type filter
        invoiced: 'all' // 'all', 'yes', 'no'
    });

    // Time settings for marker colors
    const [timeSettings, setTimeSettings] = useState({
        green: 7,
        yellow: 14,
        red: 15
    });

    // Helper function to format driver name
    const formatDriverName = (email) => {
        if (!email) return "Unknown";
        const name = email.split('@')[0];
        return name.charAt(0).toUpperCase() + name.slice(1);
    };

    // Check if user is admin (using new structure: user.uid is the document ID)
    const checkCurrentUserAdmin = async (uid) => {
        try {
            const userDocRef = doc(db, 'users', uid);
            const userDoc = await getDoc(userDocRef);

            if (userDoc.exists()) {
                const userData = userDoc.data();
                return userData.admin === true;
            }
            return false;
        } catch (error) {
            console.error("Error checking admin status:", error);
            return false;
        }
    };

    // Load company-specific time settings from localStorage
    useEffect(() => {
        if (companyId) {
            const companySettingsKey = `markerTimeSettings_${companyId}`;
            const savedSettings = localStorage.getItem(companySettingsKey);
            if (savedSettings) {
                setTimeSettings(JSON.parse(savedSettings));
            }
        }
    }, [companyId]);

    // Save time settings to localStorage
    const saveTimeSettings = useCallback((newSettings) => {
        setTimeSettings(newSettings);
        if (companyId) {
            const companySettingsKey = `markerTimeSettings_${companyId}`;
            localStorage.setItem(companySettingsKey, JSON.stringify(newSettings));
        }
    }, [companyId]);

    // Main effect: Auth state and data fetching
    useEffect(() => {
        const auth = getAuth();
        let unsubscribeJobs;

        const unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
            // Clear any previous errors when auth state changes
            setError(null);

            if (!user) {
                setIsLoading(false);
                return;
            }

            try {

                // NEW STRUCTURE: Use auth.uid directly as document ID
                const userDocRef = doc(db, 'users', user.uid);
                let userDoc = await getDoc(userDocRef);

                // If user document doesn't exist, retry with exponential backoff
                // This handles race conditions during new account creation
                if (!userDoc.exists()) {
                    const delays = [300, 600, 1200]; // Fast exponential backoff
                    for (let i = 0; i < delays.length; i++) {
                        await new Promise(resolve => setTimeout(resolve, delays[i]));
                        userDoc = await getDoc(userDocRef);
                        if (userDoc.exists()) break;
                    }

                    if (!userDoc.exists()) {
                        console.error("User document not found for UID after retries");
                        setError(new Error("User document not found. Please contact your administrator."));
                        setIsLoading(false);
                        return;
                    }
                }

                const userData = userDoc.data();
                const storedCompanyId = userData.companyId;

                setCompanyId(storedCompanyId);
                const isAdmin = await checkCurrentUserAdmin(user.uid);
                setUserIsAdmin(isAdmin);
                localStorage.setItem('companyId', storedCompanyId);

                // Look up company name (direct document read by companyId)
                const companyDocRef = doc(db, 'companies', storedCompanyId);
                const companyDoc = await getDoc(companyDocRef);

                let derivedName;
                if (companyDoc.exists()) {
                    const companyData = companyDoc.data();
                    derivedName = companyData.companyName || `Company ${storedCompanyId}`;
                } else {
                    derivedName = `Company ${storedCompanyId}`;
                }

                setCompanyName(derivedName);
                document.title = `${derivedName} - Skip Tracker`;

                // Set up Firestore listener for jobs
                const q = query(
                    collection(db, 'jobs'),
                    where('companyId', '==', storedCompanyId),
                    orderBy('timestamp', 'desc')
                );

                // Helper function to set up the snapshot listener with retry logic
                const setupJobsListener = () => {
                    unsubscribeJobs = onSnapshot(
                        q,
                        async (snapshot) => {
                            const fetchedJobs = [];

                            // Collect jobs that need driver lookup (legacy jobs without driverName)
                            const jobsNeedingDriverLookup = [];

                            for (const jobDoc of snapshot.docs) {
                                const data = jobDoc.data();

                                // Check if job has denormalized driver data (new format)
                                if (data.driverName) {
                                    // NEW: Driver name is already in the job document
                                    const lat = Number(data.latitude);
                                    const lng = Number(data.longitude);

                                    fetchedJobs.push({
                                        id: jobDoc.id,
                                        position: [lat, lng],
                                        initialTime: data.formattedDate,
                                        timestamp: data.timestamp,
                                        size: data.size,
                                        name: data.jobType || "Skip Job",
                                        description: data.wasteType || "",
                                        status: data.status,
                                        skipId: data.skipId,
                                        imageUrl: data.imageUrl,
                                        driverName: data.driverName,  // Read directly from job!
                                        driverEmail: data.driverEmail || "",  // Also available if needed
                                        streetAddress: data.address && data.address.streetAddress ? data.address.streetAddress : "",
                                        formattedAddress: data.address && data.address.formattedAddress ? data.address.formattedAddress : "",
                                        postcode: data.postcode || data.address?.postcode || "",  // Prefer top-level, fallback to nested
                                        businessName: data.businessName || data.address?.businessName || "",  // Prefer top-level, fallback to nested
                                        jobType: data.jobType,
                                        wasteType: data.wasteType,
                                        address: data.address,
                                        companyId: data.companyId,
                                        userId: data.userId,
                                        driverNote: data.driverNote || "",
                                        paymentStatus: data.paymentStatus || "Unknown",
                                        assignedDriverId: data.assignedDriverId || null,
                                        assignedDriverName: data.assignedDriverName || null,
                                        assignedAt: data.assignedAt || null,
                                        assignedBy: data.assignedBy || null,
                                        assignmentType: data.assignmentType || null,
                                        collectedBy: data.collectedBy || null,
                                        collectionTimestamp: data.collectionTimestamp || null,
                                        collectionDate: data.collectionDate || null,
                                        invoiced: data.invoiced === true
                                    });
                                } else if (data.userId) {
                                    // OLD: Legacy job without driverName - needs lookup
                                    jobsNeedingDriverLookup.push({ jobDoc, data });
                                } else {
                                    // No driver info at all
                                    const lat = Number(data.latitude);
                                    const lng = Number(data.longitude);

                                    fetchedJobs.push({
                                        id: jobDoc.id,
                                        position: [lat, lng],
                                        initialTime: data.formattedDate,
                                        timestamp: data.timestamp,
                                        size: data.size,
                                        name: data.jobType || "Skip Job",
                                        description: data.wasteType || "",
                                        status: data.status,
                                        skipId: data.skipId,
                                        imageUrl: data.imageUrl,
                                        driverName: "Unknown",
                                        streetAddress: data.address && data.address.streetAddress ? data.address.streetAddress : "",
                                        formattedAddress: data.address && data.address.formattedAddress ? data.address.formattedAddress : "",
                                        postcode: data.postcode || data.address?.postcode || "",  // Prefer top-level, fallback to nested
                                        businessName: data.businessName || data.address?.businessName || "",  // Prefer top-level, fallback to nested
                                        jobType: data.jobType,
                                        wasteType: data.wasteType,
                                        address: data.address,
                                        companyId: data.companyId,
                                        userId: data.userId,
                                        driverNote: data.driverNote || "",
                                        paymentStatus: data.paymentStatus || "Unknown",
                                        assignedDriverId: data.assignedDriverId || null,
                                        assignedDriverName: data.assignedDriverName || null,
                                        assignedAt: data.assignedAt || null,
                                        assignedBy: data.assignedBy || null,
                                        assignmentType: data.assignmentType || null,
                                        collectedBy: data.collectedBy || null,
                                        collectionTimestamp: data.collectionTimestamp || null,
                                        collectionDate: data.collectionDate || null,
                                        invoiced: data.invoiced === true
                                    });
                                }
                            }

                            // Handle legacy jobs (if any) - batch fetch driver names
                            if (jobsNeedingDriverLookup.length > 0) {
                                // Collect unique userIds for batch lookup
                                const uniqueUserIds = [...new Set(
                                    jobsNeedingDriverLookup
                                        .map(({ data }) => data.userId)
                                        .filter(id => id && typeof id === 'string' && id.length > 0)
                                )];

                                // Batch fetch all driver documents (Firestore 'in' supports up to 30 items)
                                const driverCache = new Map();
                                const chunkSize = 30;

                                for (let i = 0; i < uniqueUserIds.length; i += chunkSize) {
                                    const chunk = uniqueUserIds.slice(i, i + chunkSize);
                                    try {
                                        const driversQuery = query(
                                            collection(db, 'users'),
                                            where(documentId(), 'in', chunk)
                                        );
                                        const driversSnapshot = await getDocs(driversQuery);
                                        driversSnapshot.forEach(driverDoc => {
                                            const driverData = driverDoc.data();
                                            if (driverData.companyId === storedCompanyId) {
                                                driverCache.set(driverDoc.id, driverData);
                                            }
                                        });
                                    } catch (error) {
                                        if (error.code !== 'permission-denied') {
                                            console.warn("Error batch fetching drivers:", error.code || error.message);
                                        }
                                    }
                                }

                                // Process legacy jobs using the cache
                                for (const { jobDoc, data } of jobsNeedingDriverLookup) {
                                    const driverData = driverCache.get(data.userId);
                                    const driverName = driverData
                                        ? (driverData.name || formatDriverName(driverData.email || '') || `Driver ${data.userId.substring(0, 5)}`)
                                        : "Unknown";

                                    const lat = Number(data.latitude);
                                    const lng = Number(data.longitude);

                                    fetchedJobs.push({
                                        id: jobDoc.id,
                                        position: [lat, lng],
                                        initialTime: data.formattedDate,
                                        timestamp: data.timestamp,
                                        size: data.size,
                                        name: data.jobType || "Skip Job",
                                        description: data.wasteType || "",
                                        status: data.status,
                                        skipId: data.skipId,
                                        imageUrl: data.imageUrl,
                                        driverName: driverName,
                                        streetAddress: data.address?.streetAddress || "",
                                        formattedAddress: data.address?.formattedAddress || "",
                                        postcode: data.postcode || data.address?.postcode || "",  // Prefer top-level, fallback to nested
                                        businessName: data.businessName || data.address?.businessName || "",  // Prefer top-level, fallback to nested
                                        jobType: data.jobType,
                                        wasteType: data.wasteType,
                                        address: data.address,
                                        companyId: data.companyId,
                                        userId: data.userId,
                                        driverNote: data.driverNote || "",
                                        paymentStatus: data.paymentStatus || "Unknown",
                                        assignedDriverId: data.assignedDriverId || null,
                                        assignedDriverName: data.assignedDriverName || null,
                                        assignedAt: data.assignedAt || null,
                                        assignedBy: data.assignedBy || null,
                                        assignmentType: data.assignmentType || null,
                                        collectedBy: data.collectedBy || null,
                                        collectionTimestamp: data.collectionTimestamp || null,
                                        collectionDate: data.collectionDate || null,
                                        invoiced: data.invoiced === true
                                    });
                                }
                            }

                            setJobs(fetchedJobs);
                            setIsLoading(false);
                        },
                        (error) => {
                            console.error("Error in jobs snapshot:", error.code);

                            // If it's a permission error, retry after a short delay
                            if (error.code === 'permission-denied') {
                                setTimeout(() => {
                                    if (unsubscribeJobs) unsubscribeJobs();
                                    setupJobsListener();
                                }, 1000);
                            } else {
                                // For other errors, show the error
                                setError(error);
                                setIsLoading(false);
                            }
                        }
                    );
                };

                // Initial setup of the listener
                setupJobsListener();
            } catch (error) {
                console.error("💥 Error in auth state handler:", error);
                setError(error);
                setIsLoading(false);
            }
        });

        return () => {
            unsubscribeAuth();
            if (unsubscribeJobs) unsubscribeJobs();
        };
    }, []);

    // Get filtered jobs for MAP VIEW (delivered only)
    // Memoized with useCallback to prevent unnecessary recalculations
    const getFilteredJobsForMap = useCallback(() => {
        const currentTime = new Date();

        return jobs.filter(job => {
            // Map view: Only show delivered skips (skips currently on site)
            if (job.status !== 'delivered') {
                return false;
            }

            // Size filter
            const sizeMatch = filters.size === '' || job.size === filters.size;

            // Driver filter
            const driverMatch = filters.driver === '' || job.driverName === filters.driver;

            // Waste type filter
            const wasteTypeMatch = filters.wasteType === '' || job.wasteType === filters.wasteType;

            // Date range filter
            let daysDiff = 0;
            try {
                const jobDate = parseJobDate(job.initialTime, job.timestamp);
                if (jobDate) {
                    const timeDiff = currentTime - jobDate;
                    daysDiff = timeDiff / (1000 * 60 * 60 * 24);
                }
            } catch (err) {
                console.error("Error calculating date difference:", err);
            }

            let dateRangeMatch = true;
            if (filters.dateRange !== 'all') {
                switch(filters.dateRange) {
                    case 'recent':
                        dateRangeMatch = daysDiff <= timeSettings.green;
                        break;
                    case 'medium':
                        dateRangeMatch = daysDiff > timeSettings.green && daysDiff <= timeSettings.yellow;
                        break;
                    case 'old':
                        dateRangeMatch = daysDiff > timeSettings.yellow;
                        break;
                    default:
                        dateRangeMatch = true;
                }
            }

            // Payment status filter (multi-select)
            const paymentStatusMatch = filters.paymentStatus.includes(job.paymentStatus || 'Unknown');

            // Invoiced filter
            const invoicedMatch = filters.invoiced === 'all' ||
                (filters.invoiced === 'yes' && job.invoiced === true) ||
                (filters.invoiced === 'no' && job.invoiced !== true);

            return sizeMatch && driverMatch && wasteTypeMatch && dateRangeMatch && paymentStatusMatch && invoicedMatch;
        });
    }, [jobs, filters.size, filters.driver, filters.wasteType, filters.dateRange, filters.paymentStatus, filters.invoiced, timeSettings.green, timeSettings.yellow]);

    // Get filtered jobs for RECORDS VIEW (delivered + collected)
    // Memoized with useCallback to prevent unnecessary recalculations
    const getFilteredJobsForRecords = useCallback(() => {
        const currentTime = new Date();

        return jobs.filter(job => {
            // Status filter (Records view shows both delivered and collected)
            let statusMatch = true;
            if (filters.status !== 'all') {
                statusMatch = job.status === filters.status;
            } else {
                // When "all", show delivered and collected
                statusMatch = job.status === 'delivered' || job.status === 'collected';
            }

            // Size filter
            const sizeMatch = filters.size === '' || job.size === filters.size;

            // Driver filter
            const driverMatch = filters.driver === '' || job.driverName === filters.driver;

            // Waste type filter
            const wasteTypeMatch = filters.wasteType === '' || job.wasteType === filters.wasteType;

            // Date range filter
            let daysDiff = 0;
            try {
                const jobDate = parseJobDate(job.initialTime, job.timestamp);
                if (jobDate) {
                    const timeDiff = currentTime - jobDate;
                    daysDiff = timeDiff / (1000 * 60 * 60 * 24);
                }
            } catch (err) {
                console.error("Error calculating date difference:", err);
            }

            let dateRangeMatch = true;
            if (filters.dateRange !== 'all') {
                switch(filters.dateRange) {
                    case 'recent':
                        dateRangeMatch = daysDiff <= timeSettings.green;
                        break;
                    case 'medium':
                        dateRangeMatch = daysDiff > timeSettings.green && daysDiff <= timeSettings.yellow;
                        break;
                    case 'old':
                        dateRangeMatch = daysDiff > timeSettings.yellow;
                        break;
                    default:
                        dateRangeMatch = true;
                }
            }

            // Payment status filter (multi-select)
            const paymentStatusMatch = filters.paymentStatus.includes(job.paymentStatus || 'Unknown');

            // Invoiced filter
            const invoicedMatch = filters.invoiced === 'all' ||
                (filters.invoiced === 'yes' && job.invoiced === true) ||
                (filters.invoiced === 'no' && job.invoiced !== true);

            return statusMatch && sizeMatch && driverMatch && wasteTypeMatch && dateRangeMatch && paymentStatusMatch && invoicedMatch;
        });
    }, [jobs, filters.size, filters.driver, filters.wasteType, filters.dateRange, filters.status, filters.paymentStatus, filters.invoiced, timeSettings.green, timeSettings.yellow]);

    // Legacy function for backward compatibility (defaults to Map view behavior)
    const getFilteredJobs = getFilteredJobsForMap;

    // Get unique drivers for filter dropdown (Map view - delivered only)
    // Memoized with useCallback to prevent unnecessary recalculations
    const getUniqueDriversForMap = useCallback(() => {
        const drivers = jobs
            .filter(job => job.status === 'delivered')
            .map(job => job.driverName)
            .filter(name => name && name !== 'Unknown')
            .sort();

        return [...new Set(drivers)];
    }, [jobs]);

    // Get unique drivers for filter dropdown (Records view - delivered + collected)
    // Memoized with useCallback to prevent unnecessary recalculations
    const getUniqueDriversForRecords = useCallback(() => {
        const drivers = jobs
            .filter(job => job.status === 'delivered' || job.status === 'collected')
            .map(job => job.driverName)
            .filter(name => name && name !== 'Unknown')
            .sort();

        return [...new Set(drivers)];
    }, [jobs]);

    // Legacy function for backward compatibility
    const getUniqueDrivers = getUniqueDriversForMap;

    // Get marker color based on age
    // Accepts initialTime string and optional Firestore timestamp for fallback parsing
    // Memoized with useCallback to prevent unnecessary recalculations
    const getMarkerColor = useCallback((initialDate, timestamp) => {
        try {
            const initialDateTime = parseJobDate(initialDate, timestamp);

            if (!initialDateTime) {
                return '#F44336'; // Red for unparseable dates
            }

            const currentTime = new Date();
            const timeDiff = currentTime - initialDateTime;
            const daysDiff = timeDiff / (1000 * 60 * 60 * 24);

            if (daysDiff <= timeSettings.green) return '#4CAF50';
            if (daysDiff <= timeSettings.yellow) return '#FFC107';
            return '#F44336';
        } catch (err) {
            console.error("Error in getMarkerColor:", err);
            return '#F44336';
        }
    }, [timeSettings.green, timeSettings.yellow]);

    // Memoize stable functions to prevent unnecessary re-renders
    const resetFilters = useCallback(() => {
        setFilters({
            size: '',
            dateRange: 'all',
            driver: '',
            status: 'all',
            paymentStatus: ['Paid', 'TO PAY', 'Account', 'Unknown'],
            wasteType: '',
            invoiced: 'all'
        });
    }, []);

    // Memoize the context value to prevent re-renders when values haven't changed
    // This groups values by stability to optimize re-render behavior
    const value = useMemo(() => ({
        // Data (changes when jobs update)
        jobs,

        // Auth state (stable after initial load)
        companyId,
        companyName,
        userIsAdmin,

        // Loading state
        isLoading,
        error,

        // Filter state (changes on user interaction)
        filters,
        setFilters,
        timeSettings,
        saveTimeSettings,

        // Computed functions (memoized via useCallback)
        getFilteredJobsForMap,
        getFilteredJobsForRecords,
        getUniqueDriversForMap,
        getUniqueDriversForRecords,
        getFilteredJobs,
        getUniqueDrivers,
        getMarkerColor,
        resetFilters
    }), [
        jobs,
        companyId,
        companyName,
        userIsAdmin,
        isLoading,
        error,
        filters,
        timeSettings,
        saveTimeSettings,
        getFilteredJobsForMap,
        getFilteredJobsForRecords,
        getUniqueDriversForMap,
        getUniqueDriversForRecords,
        getFilteredJobs,
        getUniqueDrivers,
        getMarkerColor,
        resetFilters
    ]);

    return (
        <SkipsDataContext.Provider value={value}>
            {children}
        </SkipsDataContext.Provider>
    );
};
